import {Injectable} from '@angular/core';
import {IDateTimePeriod} from "../../_typing/date-time-period";
import {Series} from "../../_models/series";
import {Observable, Subject, switchMap} from "rxjs";
import {DateTimeInstanceService} from "../../services/date-time-instance.service";
import {ApiService} from "../../services/api/api.service";
import {SeriesDataService} from "../../services/series_data.service";
import {ChartConfigTranslationService} from "../../services/chart-config-translation.service";
import {FormatNumberPipe} from "../../shared/pipes";
import {
    GenericChartTileConfiguration,
    SeriesSummaryChartTileConfiguration
} from "../chart-config/generic-chart-tile.configuration";
import {map, mergeMap, tap} from "rxjs/operators";
import {
    GetSeriesData,
    SeriesGetSeriesData,
    SeriesSummaries,
    SeriesSummary,
    SeriesSummarySeries
} from "../../_models/api/series-summary";
import {KeyMap} from "../../_typing/generic-types";
import {BarChartConfiguration, SeriesSummaryChartConfiguration} from "../chart-config/chart-configuration";

export interface SeriesSummaryChartData {
    chartConfig: SeriesSummaryChartConfiguration;
    seriesSummaryData: SeriesSummary[];
    seriesDict: KeyMap<SeriesSummarySeries>;
}

@Injectable({
    providedIn: 'root'
})
export class SeriesSummaryChartService {
    dtp: IDateTimePeriod;
    seriesFull: Series[];

    constructor(private dateInst: DateTimeInstanceService,
                private api: ApiService,
                private seriesData: SeriesDataService,
                private chartConfigService: ChartConfigTranslationService) {
    }

    getChart(tileConfig: SeriesSummaryChartTileConfiguration): Observable<SeriesSummaryChartData> {
        return this._getChartData(tileConfig);
    }

    private _getChartData(tileConfig: SeriesSummaryChartTileConfiguration): Observable<SeriesSummaryChartData> {
        this.dtp = this.dateInst.dtp;
        const seriesIds = tileConfig.series_list.map(series => series.id);
        return this.seriesData.getSeriesListByIds(seriesIds)
            .pipe(tap(response => {
                    this.seriesFull = response.data;
                }),
                switchMap(() => {
                    return this.getSeriesSummary(tileConfig, seriesIds)
                }), map((data: SeriesSummary[]) => {
                    // Reverse data for horizontal bars so series order matches configuration (Plotly displays bottom-to-top)
                    const reversedData = [...data].reverse();
                    const seriesSummaryDict = this.seriesData.mapSeriesToSeriesSummary(this.seriesFull, reversedData);
                    return ({
                        chartConfig: this.configureChartConfig(tileConfig, reversedData, seriesSummaryDict),
                        seriesSummaryData: reversedData,
                        seriesDict: seriesSummaryDict
                    })
                }))
    }

    private getSeriesSummary(tileConfig: SeriesSummaryChartTileConfiguration, seriesIds: string[]): Observable<SeriesSummaries> {
        const fullPeriodColumn = `Full ${tileConfig.period_type}`;
        return this.seriesData.getSeriesSummary(this.dtp, seriesIds, null, tileConfig.columns.concat([fullPeriodColumn]), [tileConfig.estimate_type])
    }

    private configureChartConfig(tileConfig: SeriesSummaryChartTileConfiguration, seriesSummaryData: SeriesSummary[], seriesDict: KeyMap<SeriesSummarySeries>): SeriesSummaryChartConfiguration {
        const chartConfig: SeriesSummaryChartConfiguration = {
            type: 'bar',
            key_values: tileConfig.columns,
            x_values: {series: seriesSummaryData.map(d => d.Description || d.Name)},
            y_values: this.getYValues(tileConfig, seriesSummaryData),
            names: this.getColumnNameMap(tileConfig),
            orientation: tileConfig.orientation || 'h',
            barmode: 'group',
            colours: this.chartConfigService.getColumnColours(tileConfig),
            styles: this.chartConfigService.getColumnStyles(tileConfig, tileConfig.columns),
            labels: tileConfig.labels,
            text_values: this.getYValues(tileConfig, seriesSummaryData),
            text_orientation: this.chartConfigService.getTextOrientation(tileConfig),
            no_ticks: tileConfig.y_max_ticks_set ? tileConfig.y_max_ticks : null,
            tick_space: tileConfig.y_spacing_set ? tileConfig.y_spacing : null,
            y_axis_format: this.chartConfigService.getYAxisFormat(tileConfig),
            y_min: this.chartConfigService.getYMin(tileConfig),
            y_max: this.chartConfigService.getYMax(tileConfig),
        }

        return chartConfig
    }

    private getYValues(tileConfig: SeriesSummaryChartTileConfiguration, seriesSummaryData: SeriesSummary[]) {
        let columnDataMap: KeyMap<string[]> = {};
        tileConfig.columns.forEach(column => {
            columnDataMap[column] = seriesSummaryData.map(d => String(d[column]));
        })
        return columnDataMap;
    }

    private getColumnNameMap(tileConfig: SeriesSummaryChartTileConfiguration) {
        let nameMap = {};
        tileConfig.columns.map(d => {
            nameMap[d] = tileConfig.column_formats[d]?.title || d;
        })
        return nameMap;
    }

}
